// ListFilter.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useRef, useState } from 'react';
import { Drawer, Segmented, Tooltip, theme } from 'antd';
import translateX from 'utils/translateX';
import { DeleteOutlined, EyeInvisibleOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useModuleContext } from '../core/ModuleProvider';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// FUNCTIONS
// *******************************************************************************

// *******************************************************************************
// COMPONENT
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ListFilter = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { selectedDocument, show, setShow } = props;
  const storedListSettings = useZustand('module', 'listSettings');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const { listContext } = useModuleContext();

  // Support both JarvislyModule ListProvider (handleFilterDrawerChanges) and
  // core ListProvider (setDrawerFilter)
  const handleFilterDrawerChanges =
    listContext?.handleFilterDrawerChanges || listContext?.setDrawerFilter;

  // Get setLoading to show table loading immediately on filter change
  const setLoading = listContext?.setLoading;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const filterComponent = storedListSettings?.filterComponent || null;
  const filterBodyComponent = storedCurrentPage?.filterDrawer?.components?.body || null;
  const folderSection = storedCurrentPage?.filterDrawer?.folderSection || {};
  const folderSectionTitle = folderSection.title || 'folders';
  const inboxLabel = folderSection.inboxLabel || 'documents';
  const archiveLabel = folderSection.archiveLabel || 'archived';
  const recycleLabel = folderSection.recycleLabel || 'recycle';

  // Get initial folder from query (URL 'q' param decoded) or store or default
  const initialFolder =
    listContext?.query?.drawer?.folder || storedListSettings?.filterDrawer?.folder || 'inbox';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedFolder, setSelectedFolder] = useState(initialFolder);

  // Track if we're in the middle of a local change to avoid sync conflicts
  const isLocalChangeRef = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync local state with store/query only for external changes (e.g., F5, navigation)
  useEffect(() => {
    // Skip sync if we just made a local change
    if (isLocalChangeRef.current) return;

    const queryFolder = listContext?.query?.drawer?.folder;
    const storeFolder = storedListSettings?.filterDrawer?.folder;
    const folder = queryFolder || storeFolder;

    if (folder && folder !== selectedFolder) {
      setSelectedFolder(folder);
    }
  }, [listContext?.query?.drawer?.folder, storedListSettings?.filterDrawer?.folder]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFilterChange = value => {
    // Mark as local change to prevent sync effect from reverting
    isLocalChangeRef.current = true;

    // Update local state immediately for instant visual feedback
    setSelectedFolder(value);

    // Show loading on table immediately
    setLoading?.(true);

    // Use setTimeout to ensure UI updates before API call
    // This allows React to flush the state update and repaint before the request
    setTimeout(() => {
      handleFilterDrawerChanges?.({ folder: value });

      // Reset flag after a delay to allow store to fully sync
      setTimeout(() => {
        isLocalChangeRef.current = false;
      }, 1000);
    }, 0);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Drawer
      title={translateX('filter')}
      size={300}
      placement="right"
      onClose={() => setShow(false)}
      closable={false}
      open={show}
    >
      {/* FOLDER */}
      <div>
        <h5 className="mb-3 text-muted">{translateX(folderSectionTitle)}</h5>
        <Segmented
          block
          size="small"
          value={selectedFolder}
          onChange={handleFilterChange}
          options={[
            {
              label: (
                <Tooltip title={translateX(inboxLabel)} placement="top">
                  <div>
                    <FolderOpenOutlined />
                  </div>
                </Tooltip>
              ),
              value: 'inbox',
            },
            {
              label: (
                <Tooltip title={translateX(archiveLabel)} placement="top">
                  <div>
                    <EyeInvisibleOutlined />
                  </div>
                </Tooltip>
              ),
              value: 'archive',
            },
            {
              label: (
                <Tooltip title={translateX(recycleLabel)} placement="top">
                  <div>
                    <DeleteOutlined />
                  </div>
                </Tooltip>
              ),
              value: 'recycle',
            },
          ]}
        />
      </div>
      {/* FOLDER */}

      {/* MODULE FILTER COMPONENT */}
      {filterComponent && filterComponent?.(props)}
      {filterBodyComponent && filterBodyComponent?.({ ...props, listContext })}
      {/* MODULE FILTER COMPONENT */}
    </Drawer>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ListFilter;
